/**
 * Draws Material Design Icons (`mdi-` names) as inline SVG instead of the MDI
 * icon font, whose stylesheet held a rule for each of its ~7,400 icons and was
 * parsed on every load before the app could start.
 *
 * Paths for the icons the app and Vuetify use are bundled (`MDI_PATHS`). Any
 * other `mdi-` name — from data, or a new icon not added to the map — loads
 * the full icon set once, and the icon appears as soon as it arrives.
 */
import { defineComponent, h, shallowRef } from 'vue';
import { mdi as svgIconSet } from 'vuetify/iconsets/mdi-svg';
import type * as MdiIcons from '@mdi/js';
import { MDI_PATHS } from '@/constants/mdiIcons';

const fullSet = shallowRef<typeof MdiIcons | null>(null);
let fullSetLoad: Promise<void> | null = null;
const reported = new Set<string>();

export const MdiSvgIcon = defineComponent({
  name: 'MdiSvgIcon',
  props: {
    icon: { type: String, required: true },
    tag: { type: String, default: 'i' }
  },
  setup(props) {
    return () =>
      h(svgIconSet.component, { tag: props.tag, icon: mdiPath(props.icon) });
  }
});

/** The SVG path of an `mdi-` icon; empty until an unbundled one has loaded. */
function mdiPath(name: string): string {
  const bundled = MDI_PATHS[name];
  if (bundled) return bundled;

  report(name);
  if (!fullSet.value) {
    loadFullSet();
    return '';
  }
  const path: unknown = Reflect.get(fullSet.value, exportName(name));
  return typeof path === 'string' ? path : '';
}

function loadFullSet(): void {
  // the CommonJS build, so the full set stays out of the bundle the named imports go into
  fullSetLoad ??= import('@mdi/js/commonjs/mdi.js')
    .then(icons => {
      fullSet.value = icons;
    })
    .catch(() => {
      // let the next icon that needs it try again
      fullSetLoad = null;
    });
}

/** `mdi-format-rotate-90` → `mdiFormatRotate90`, the name `@mdi/js` exports. */
function exportName(name: string): string {
  return name
    .slice('mdi-'.length)
    .split('-')
    .reduce(
      (out, part) => out + part.charAt(0).toUpperCase() + part.slice(1),
      'mdi'
    );
}

function report(name: string): void {
  if (!import.meta.env.DEV || reported.has(name)) return;
  reported.add(name);
  console.warn(
    `[icons] ${name} is not in MDI_PATHS (src/constants/mdiIcons.ts); loading the full icon set`
  );
}
